import React, { useMemo, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Activity, Eye, EyeOff, Check, X } from "lucide-react";

function Shell({ title, subtitle, children }) {
  return (
    <div className="grid-bg" style={{ minHeight: "100vh", display: "grid", placeItems: "center", padding: 24 }}>
      <div style={{ width: "100%", maxWidth: 460 }}>
        <Link to="/" style={{ display: "flex", alignItems: "center", gap: 10, textDecoration: "none", marginBottom: 28 }}>
          <div style={{ width: 32, height: 32, background: "var(--amber-500)", display: "grid", placeItems: "center", borderRadius: 2 }}>
            <Activity size={18} color="var(--navy-900)" strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-serif-display" style={{ fontSize: 16, fontWeight: 700, color: "var(--ink)", lineHeight: 1 }}>CanadaMRO</div>
            <div className="font-mono" style={{ fontSize: 10, color: "var(--ink-mute)", letterSpacing: ".15em", marginTop: 2 }}>EXCHANGE</div>
          </div>
        </Link>
        <div className="card" style={{ padding: 32 }}>
          <h1 className="font-serif-display" style={{ fontSize: 26, fontWeight: 600, margin: 0 }}>{title}</h1>
          {subtitle && <p style={{ color: "var(--ink-dim)", fontSize: 14, marginTop: 8 }}>{subtitle}</p>}
          <div style={{ marginTop: 24 }}>{children}</div>
        </div>
      </div>
    </div>
  );
}

function PasswordInput({ value, onChange, autoComplete, testId, placeholder, required = true }) {
  const [show, setShow] = useState(false);
  return (
    <div className="pw-toggle-wrap">
      <input
        className="input"
        type={show ? "text" : "password"}
        autoComplete={autoComplete}
        minLength={8}
        required={required}
        data-testid={testId}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
      />
      <button
        type="button"
        className="pw-toggle-btn"
        onClick={() => setShow(s => !s)}
        aria-label={show ? "Hide password" : "Show password"}
        data-testid={`${testId}-toggle`}
      >
        {show ? <EyeOff size={16} /> : <Eye size={16} />}
      </button>
    </div>
  );
}

function scorePassword(pw) {
  if (!pw) return { score: 0, label: "" };
  let s = 0;
  if (pw.length >= 8) s += 1;
  if (pw.length >= 12) s += 1;
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) s += 1;
  if (/\d/.test(pw)) s += 1;
  if (/[^A-Za-z0-9]/.test(pw)) s += 1;
  const labels = ["weak", "weak", "fair", "fair", "good", "good"];
  return { score: Math.min(s, 5), label: labels[s] || "good" };
}

function PasswordStrength({ pw }) {
  const { score, label } = useMemo(() => scorePassword(pw), [pw]);
  if (!pw) return null;
  const tier = score <= 2 ? "weak" : score === 3 ? "fair" : "good";
  return (
    <div className="pw-strength" data-testid="pw-strength">
      <div className="pw-bar" aria-hidden>
        {[0, 1, 2, 3, 4].map(i => (
          <span
            key={i}
            className={`pw-seg ${i < score ? `pw-seg-on-${tier}` : ""}`}
          />
        ))}
      </div>
      <div className={`pw-strength-label`} style={{
        color: tier === "weak" ? "#ef4444" : tier === "fair" ? "#f59e0b" : "#10b981"
      }} data-testid="pw-strength-label">
        Strength: {label}
      </div>
    </div>
  );
}

function PasswordChecklist({ pw, pw2 }) {
  const checks = [
    { ok: pw.length >= 8, label: "At least 8 characters" },
    { ok: /[A-Z]/.test(pw) && /[a-z]/.test(pw), label: "Mix of upper- and lower-case letters" },
    { ok: /\d/.test(pw), label: "At least one number" },
    { ok: pw === pw2 && pw.length > 0, label: "Both fields match" },
  ];
  return (
    <ul className="pw-checks" style={{ listStyle: "none", margin: 0, padding: 0 }} data-testid="pw-checks">
      {checks.map((c, i) => (
        <li key={i} className={`pw-check ${c.ok ? "pw-check-ok" : ""}`}>
          {c.ok ? <Check size={13} /> : <X size={13} />} {c.label}
        </li>
      ))}
    </ul>
  );
}

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const emailValid = useMemo(() => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()), [email]);

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setMsg(""); setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setMsg("If that email exists in our system, a reset link has been sent. Check your inbox.");
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  return (
    <Shell title="Forgot your password?" subtitle="We'll email you a link to set a new one.">
      <form onSubmit={submit} style={{ display: "grid", gap: 16 }}>
        <div className="field">
          <label className="label">Email</label>
          <input
            className={`input ${email && !emailValid ? "input-error" : ""}`}
            data-testid="fp-email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="you@company.ca"
          />
          {email && !emailValid && <div className="field-error">Enter a valid email address.</div>}
        </div>
        {msg && <div style={{ color: "var(--emerald)", fontSize: 13 }} data-testid="fp-message">{msg}</div>}
        {err && <div style={{ color: "var(--red)", fontSize: 13 }} data-testid="fp-error">{err}</div>}
        <button
          className="btn btn-primary"
          data-testid="fp-submit"
          disabled={busy || !emailValid}
          style={{ justifyContent: "center", padding: 12 }}
        >
          {busy ? "Sending…" : "Send reset link"}
        </button>
        <Link to="/login" style={{ color: "var(--amber-400)", fontSize: 13, textAlign: "center" }} data-testid="fp-back-login">Back to login</Link>
      </form>
    </Shell>
  );
}

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [touched2, setTouched2] = useState(false);
  const token = params.get("token") || "";

  const lengthOk = pw.length >= 8;
  const matches = pw && pw === pw2;
  const canSubmit = !!token && lengthOk && matches && !busy;

  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (!lengthOk) { setErr("Password must be at least 8 characters."); return; }
    if (pw !== pw2) { setErr("Passwords do not match."); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      nav("/login?reset=ok");
    } catch (e) {
      const m = formatApiError(e.response?.data?.detail) || e.message;
      setErr(m === "Invalid token" || m === "Token expired"
        ? "This reset link is no longer valid. Please request a new one."
        : m);
    }
    finally { setBusy(false); }
  };

  if (!token) {
    return (
      <Shell title="Reset link missing" subtitle="This page needs a reset token in the URL.">
        <div style={{ display: "grid", gap: 14 }}>
          <p style={{ fontSize: 13, color: "var(--ink-dim)" }}>
            If you arrived here directly, please request a new reset link.
          </p>
          <Link to="/forgot-password" className="btn btn-primary" style={{ justifyContent: "center", padding: 12 }} data-testid="rp-request-new">
            Request a new reset link
          </Link>
          <Link to="/login" style={{ color: "var(--amber-400)", fontSize: 13, textAlign: "center" }}>Back to login</Link>
        </div>
      </Shell>
    );
  }

  return (
    <Shell title="Set a new password" subtitle="Choose a strong password you don't reuse elsewhere.">
      <form onSubmit={submit} style={{ display: "grid", gap: 16 }}>
        <div className="field">
          <label className="label">New password</label>
          <PasswordInput value={pw} onChange={setPw} autoComplete="new-password" testId="rp-password" placeholder="At least 8 characters" />
          <PasswordStrength pw={pw} />
        </div>
        <div className="field">
          <label className="label">Confirm password</label>
          <PasswordInput value={pw2} onChange={(v) => { setPw2(v); setTouched2(true); }} autoComplete="new-password" testId="rp-password2" placeholder="Re-enter the password" />
          {touched2 && pw2 && pw !== pw2 && <div className="field-error" data-testid="rp-mismatch">Passwords do not match.</div>}
        </div>
        <PasswordChecklist pw={pw} pw2={pw2} />
        {err && <div style={{ color: "var(--red)", fontSize: 13 }} data-testid="rp-error">{err}</div>}
        <button
          className="btn btn-primary"
          disabled={!canSubmit}
          data-testid="rp-submit"
          style={{ justifyContent: "center", padding: 12 }}
        >
          {busy ? "Saving…" : "Save password"}
        </button>
        <Link to="/login" style={{ color: "var(--amber-400)", fontSize: 13, textAlign: "center" }}>Back to login</Link>
      </form>
    </Shell>
  );
}
